import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { FlexColumn, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { FbsMarketingStats } from './components/fbs-marketing-stats/fbs-marketing-stats';
import { ZoneComparisonMetricsTable } from './components/zone-comparison-metrics-table/zone-comparison-metrics-table';
import { ZoneComparisonUsMap } from './components/zone-comparison-us-map/zone-comparison-us-map';
import { ZoneComparisonWarehouseSelector } from './components/zone-comparison-warehouse-selector/zone-comparison-warehouse-selector';
import { messages } from './messages';
import { calculateStateZoneMap } from './utils/calculate-state-zone-map';
import { getWarehouseNameMessageDescriptor } from './utils/get-warehouse-name';
import style from './zone-comparison-section.module.less';
import { useOnComponentMount } from '../../../../../hooks/use-on-component-mount';
import Global from '../../../../../store/global-selectors';
import { getWarehouseZoneCoverage } from '../../../fbs.api';

/**
 * @typedef {import('../../../fbs-api-types.d.ts').ZoneCoverageWarehouse} ZoneCoverageWarehouse
 * @typedef {import('../../../fbs-api-types.d.ts').StateZoneInfoMap} StateZoneInfoMap
 */

/**
 * @param {object} props
 * @param {React.ReactNode} props.fallbackImage
 * @returns {React.JSX.Element}
 */
export const ZoneComparisonSection = ({ fallbackImage }) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const [hasError, setHasError] = useAsyncSafeState(false);
  const [sellerWarehouse, setSellerWarehouse] = useAsyncSafeState(/** @type {ZoneCoverageWarehouse | null} */ (null));
  const [fbsWarehouses, setFbsWarehouses] = useAsyncSafeState(
    /** @type {Array<{id: string, name: string, coordinates: [number, number]}>} */ ([]),
  );
  const [stateZoneInfo, setStateZoneInfo] = useAsyncSafeState(/** @type {StateZoneInfoMap | null} */ (null));
  const [selectedFbsWarehouseIds, setSelectedFbsWarehouseIds] = useAsyncSafeState(/** @type {string[]} */ ([]));
  const defaultShipFrom = useSelector(Global.selectSellerDefaultShipFrom);

  useOnComponentMount(() => {
    const warehouseZoneCoverageRequest = {
      postalCode: defaultShipFrom?.originAddress?.postalCode ?? '',
      state: defaultShipFrom?.originAddress?.state ?? '',
    };

    getWarehouseZoneCoverage(warehouseZoneCoverageRequest)
      .then((response) => {
        setSellerWarehouse(response.sellerWarehouse);
        setStateZoneInfo(response.stateZoneInfo);

        const enrichedFbsWarehouses = response.fbsWarehouses.map((warehouse) => {
          const warehouseMessage = getWarehouseNameMessageDescriptor(warehouse.id);

          return {
            id: warehouse.id,
            name: intl.formatMessage(warehouseMessage),
            coordinates: [warehouse.longitude, warehouse.latitude],
          };
        });
        setFbsWarehouses(enrichedFbsWarehouses);
        setSelectedFbsWarehouseIds(response.recommendedWarehouseIds);
      })
      .catch(() => {
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  });

  const handleWarehouseToggle = useCallback(
    (warehouseId) => {
      setSelectedFbsWarehouseIds((prev) => {
        return prev.includes(warehouseId) ? prev.filter((id) => id !== warehouseId) : [...prev, warehouseId];
      });
    },
    [setSelectedFbsWarehouseIds],
  );

  const baselineStateZoneMap = useMemo(() => {
    const baselineWarehouseIds = sellerWarehouse ? [sellerWarehouse.id] : [];
    return calculateStateZoneMap(baselineWarehouseIds, stateZoneInfo);
  }, [sellerWarehouse, stateZoneInfo]);

  const currentStateZoneMap = useMemo(() => {
    const selectedWarehouseIds = [...selectedFbsWarehouseIds];
    if (sellerWarehouse) {
      selectedWarehouseIds.push(sellerWarehouse.id);
    }
    return calculateStateZoneMap(selectedWarehouseIds, stateZoneInfo);
  }, [sellerWarehouse, selectedFbsWarehouseIds, stateZoneInfo]);

  return (
    <div className={style['zone-comparison-section']}>
      <FlexColumn className={style['header-text']} gap={Gap.Space8}>
        <Text textType={TextType.H3} value={messages.welcome} />
        <Text isBold textType={TextType.H1} value={messages.headerTitle} />
        <div>
          <Text textType={TextType.Description} value={messages.descriptionLine1} />
          <Text textType={TextType.Description} value={messages.descriptionLine2} />
        </div>
      </FlexColumn>
      {isLoading ? <LoadingIndicator className={style['loading-indicator']} /> : null}
      {!isLoading && hasError ? fallbackImage : null}
      {!isLoading && !hasError && (
        <div className={style['map-and-table-container']}>
          <ZoneComparisonUsMap
            fbsWarehouses={fbsWarehouses}
            onWarehouseToggle={handleWarehouseToggle}
            selectedFbsWarehouseIds={selectedFbsWarehouseIds}
            sellerWarehouse={sellerWarehouse}
            stateZoneMap={currentStateZoneMap}
          />
          <div className={style['selector-and-table-container']}>
            <ZoneComparisonWarehouseSelector
              onSelectionChange={setSelectedFbsWarehouseIds}
              selectedWarehouseIds={selectedFbsWarehouseIds}
              warehouses={fbsWarehouses}
            />
            <ZoneComparisonMetricsTable baselineStateZoneMap={baselineStateZoneMap} currentStateZoneMap={currentStateZoneMap} />
          </div>
        </div>
      )}
      <FbsMarketingStats />
      <div className={style['more-info']}>
        <Text textType={TextType.Description} value={messages.transparentBilling} />
        <Text textType={TextType.Description} value={messages.eligibilityText} />
      </div>
    </div>
  );
};
